import {
  MOBILE_CHAT_SNAP_DURATION,
  MOBILE_CHAT_SNAP_EASING,
} from "./mobile-chat-sheet-snap.js";

/** 面板和遮罩共用开始时间与完成屏障；不依赖Vant的全局transform和独立Overlay。 */
export function createMobileChatSheetMotion({
  panel,
  backdrop,
  viewport,
  reducedMotion = () => false,
  clock = window,
}) {
  let revision = 0,
    animations = [],
    fallbackFrame = null,
    fallbackResolve = null;
  let state = { height: 0, top: 0, y: 0, opacity: 0, radius: 16 };
  const transform = (y) => `translate3d(-50%, ${y}px, 0)`;
  const apply = (value) => {
    state = { ...value };
    Object.assign(panel.style, {
      height: value.height + "px",
      top: value.top + "px",
      transform: transform(value.y),
      borderTopLeftRadius: value.radius + "px",
      borderTopRightRadius: value.radius + "px",
    });
    backdrop.style.opacity = String(value.opacity);
  };
  const read = () => {
    if (!clock.getComputedStyle) return { ...state };
    const p = clock.getComputedStyle(panel),
      b = clock.getComputedStyle(backdrop);
    const top = parseFloat(p.top),
      height = parseFloat(p.height),
      opacity = parseFloat(b.opacity);
    return {
      ...state,
      top: Number.isFinite(top) ? top : state.top,
      height: Number.isFinite(height) ? height : state.height,
      y:
        Number.isFinite(top) && panel.getBoundingClientRect
          ? panel.getBoundingClientRect().top - top
          : state.y,
      opacity: Number.isFinite(opacity) ? opacity : state.opacity,
      radius: parseFloat(p.borderTopLeftRadius) || 0,
    };
  };
  const freeze = () => {
    const current = read();
    revision++;
    // 先固定当前计算样式，再撤销旧effect，反向操作也不会跳回旧目标。
    apply(current);
    animations.forEach((animation) => animation.cancel());
    animations = [];
    if (fallbackFrame != null) clock.cancelAnimationFrame(fallbackFrame);
    fallbackFrame = null;
    fallbackResolve?.(false);
    fallbackResolve = null;
    return current;
  };
  // cubic-bezier(.2,.8,.2,1)，二分求x对应参数，回退动画与WAAPI同节奏。
  const ease = (progress) => {
    let low = 0,
      high = 1,
      t = progress;
    for (let i = 0; i < 14; i++) {
      t = (low + high) / 2;
      const x =
        3 * (1 - t) ** 2 * t * 0.2 + 3 * (1 - t) * t ** 2 * 0.2 + t ** 3;
      if (x < progress) low = t;
      else high = t;
    }
    return 3 * (1 - t) ** 2 * t * 0.8 + 3 * (1 - t) * t ** 2 + t ** 3;
  };
  const transition = async (target, duration = MOBILE_CHAT_SNAP_DURATION) => {
    const from = freeze(),
      token = revision;
    const to = { ...from, ...target };
    if (reducedMotion() || duration <= 0) {
      apply(to);
      return true;
    }
    const options = { duration, easing: MOBILE_CHAT_SNAP_EASING, fill: "both" };
    if (panel.animate && backdrop.animate) {
      try {
        const frame = (value) => ({
          height: value.height + "px",
          top: value.top + "px",
          transform: transform(value.y),
          borderTopLeftRadius: value.radius + "px",
          borderTopRightRadius: value.radius + "px",
        });
        const sheet = panel.animate([frame(from), frame(to)], options);
        animations.push(sheet);
        if (!sheet.finished?.then)
          throw new Error("Animation completion is unavailable");
        sheet.finished.catch(() => {});
        const shade = backdrop.animate(
          [{ opacity: from.opacity }, { opacity: to.opacity }],
          options
        );
        animations.push(shade);
        if (!shade.finished?.then)
          throw new Error("Animation completion is unavailable");
        shade.finished.catch(() => {});
        const start = clock.document?.timeline?.currentTime;
        if (Number.isFinite(start)) {
          sheet.startTime = start;
          shade.startTime = start;
        }
        await Promise.all([sheet.finished, shade.finished]);
        if (token !== revision) return false;
        apply(to);
        animations.forEach((animation) => animation.cancel());
        animations = [];
        return true;
      } catch {
        if (token !== revision) return false;
        animations.forEach((animation) => animation.cancel());
        animations = [];
        apply(from);
      }
    }
    // 旧WebView：一条rAF时间线同时写位移/尺寸/透明度，不创建两个独立计时器。
    return new Promise((resolve) => {
      fallbackResolve = resolve;
      let start = null;
      const tick = (time) => {
        if (token !== revision) return;
        if (start == null) start = time;
        const progress = Math.min(1, Math.max(0, (time - start) / duration));
        const amount = ease(progress),
          current = {};
        for (const key of Object.keys(from))
          current[key] = from[key] + (to[key] - from[key]) * amount;
        apply(progress === 1 ? to : current);
        if (progress === 1) {
          fallbackFrame = null;
          fallbackResolve = null;
          resolve(true);
        } else fallbackFrame = clock.requestAnimationFrame(tick);
      };
      fallbackFrame = clock.requestAnimationFrame(tick);
    });
  };
  return {
    prepare(geometry) {
      freeze();
      apply({ ...state, ...geometry, y: geometry.height, opacity: 0 });
    },
    open(geometry) {
      return transition({ ...geometry, y: 0, opacity: 1 });
    },
    close(duration) {
      const current = read();
      return transition(
        {
          y: Math.max(current.height, viewport().bottom - current.top),
          opacity: 0,
        },
        duration
      );
    },
    resize(geometry) {
      return transition({ ...geometry, y: 0, opacity: 1 });
    },
    setGeometry(geometry) {
      freeze();
      apply({ ...state, ...geometry, y: 0, opacity: 1 });
    },
    freeze,
    dispose() {
      freeze();
    },
  };
}
